// src/components/ManageLogin.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface Login {
  SchoolCode: string;
  Login: string;
  FName: string;
  LName: string;
  RegisteredDate: string;
  ExpiryDate: string;
  LoggedOn: string;
  Category: string;
}

interface TableDependency {
  tableName: string;
  tableDesc: string;
  recCount: number;
  remarks: string;
}

const API_BASE = process.env.REACT_APP_DOCUMENT_LINK;

const EL_ManageLogin: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // State for UI
  const [activeDiv, setActiveDiv] = useState<'list' | 'delete'>('list');
  const [schools, setSchools] = useState<string[]>([]);
  const [selectedSchool, setSelectedSchool] = useState('');
  const [familyName, setFamilyName] = useState('');
  const [dateType, setDateType] = useState<'reg' | 'expiry' | 'logged' | ''>('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [logins, setLogins] = useState<Login[]>([]);
  const [selectedLogin, setSelectedLogin] = useState<Login | null>(null);
  const [dependencies, setDependencies] = useState<TableDependency[]>([]);
  const [dicFileName, setDicFileName] = useState('');
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [action, setAction] = useState<string>('');

  // Refs for table div
  const tableDivRef = useRef<HTMLDivElement>(null);

  // Load initial data
  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_BASE}/api/WEBManage/manage-login/`);
        if (!response.ok) throw new Error('Failed to load initial data');

        const data = await response.json();
        if (data.success) {
          setSchools(data.schools);
          setActiveDiv('list');
        } else {
          setErrorMsg(data.error || 'Failed to load initial data');
        }
      } catch (err) {
        setErrorMsg(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  // Load login list when filters change
  useEffect(() => {
    if (activeDiv === 'list' && selectedSchool) {
      const fetchLogins = async () => {
        setLoading(true);
        try {
          const params = new URLSearchParams();
          params.append('schoolCode', selectedSchool);
          if (familyName) params.append('familyName', familyName);
          if (dateType) {
            params.append('dateType', dateType);
            if (dateFrom) params.append('dateFrom', dateFrom);
            if (dateTo) params.append('dateTo', dateTo);
          }

          const response = await fetch(`${API_BASE}/api/WEBManage/manage-login/list/?${params.toString()}`);
          if (!response.ok) throw new Error('Failed to load logins');

          const data = await response.json();
          if (data.success) {
            setLogins(data.logins);

            // Set table dimensions after a short delay
            setTimeout(() => {
              if (tableDivRef.current) {
                const clientHeight = document.documentElement.clientHeight;
                const divTitle = document.getElementById('divTitle');
                const divButtons = document.getElementById('divButtons');
                const divFilter = document.getElementById('divFilter');

                let height = clientHeight - 50;
                if (divTitle) height -= divTitle.offsetHeight;
                if (divButtons) height -= divButtons.offsetHeight;
                if (divFilter) height -= divFilter.offsetHeight;

                tableDivRef.current.style.height = `${height}px`;
                tableDivRef.current.style.width = `${document.documentElement.clientWidth - 28}px`;
              }
            }, 100);
          } else {
            setErrorMsg(data.error || 'Failed to load logins');
          }
        } catch (err) {
          setErrorMsg(err instanceof Error ? err.message : 'An error occurred');
        } finally {
          setLoading(false);
        }
      };

      fetchLogins();
    }
  }, [activeDiv, selectedSchool, familyName, dateType, dateFrom, dateTo]);

  // Show error message if present
  useEffect(() => {
    if (errorMsg) {
      alert(errorMsg);
      setErrorMsg('');
    }
  }, [errorMsg]);

  // Handle school selection
  const handleSchoolChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedSchool(e.target.value);
  };

  // Handle date type selection
  const handleDateTypeChange = (type: 'reg' | 'expiry' | 'logged') => {
    setDateType(type);
  };

  // Handle Go button click
  const fnGo = () => {
    if (!selectedSchool) {
      setErrorMsg("Please Select a School");
      return false;
    }
    return true;
  };

  // Handle row selection
  const handleRowSelect = (login: Login) => {
    setSelectedLogin(login);
  };

  // Handle Delete button click
  const fnDelete = () => {
    if (!selectedLogin) {
      setErrorMsg("Please select a row to Delete");
      return false;
    }

    setAction('DELETE');
    setActiveDiv('delete');
    checkDeleteDependencies();
    return true;
  };

  // Check for dependencies before deletion
  const checkDeleteDependencies = async () => {
    if (!selectedLogin) return;

    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('schoolCode', selectedLogin.SchoolCode);
      params.append('login', selectedLogin.Login);
      params.append('familyCode', 'A'); // Default family code

      const response = await fetch(`${API_BASE}/api/WEBManage/manage-login/delete-check/?${params.toString()}`);
      if (!response.ok) throw new Error('Failed to check dependencies');

      const data = await response.json();
      if (data.success) {
        setDependencies(data.tables);
        setDicFileName(data.dicFileName || '');
      } else {
        setErrorMsg(data.error || 'Failed to check dependencies');
        setActiveDiv('list');
      }
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'An error occurred');
      setActiveDiv('list');
    } finally {
      setLoading(false);
    }
  };

  // Handle delete confirmation
  const fn_DeleteOK = async () => {
    if (!selectedLogin) return false;

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/WEBManage/manage-login/delete/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          schoolCode: selectedLogin.SchoolCode,
          login: selectedLogin.Login,
          familyCode: 'A'
        })
      });

      if (!response.ok) throw new Error('Failed to delete login');

      const data = await response.json();
      if (data.success) {
        alert(data.message);
        setActiveDiv('list');
        // Refresh the login list
        const params = new URLSearchParams();
        params.append('schoolCode', selectedSchool);
        if (familyName) params.append('familyName', familyName);
        if (dateType) {
          params.append('dateType', dateType);
          if (dateFrom) params.append('dateFrom', dateFrom);
          if (dateTo) params.append('dateTo', dateTo);
        }

        const listResponse = await fetch(`${API_BASE}/api/WEBManage/manage-login/list/?${params.toString()}`);
        if (listResponse.ok) {
          const listData = await listResponse.json();
          if (listData.success) {
            setLogins(listData.logins);
          }
        }
      } else {
        setErrorMsg(data.error || 'Failed to delete login');
      }
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
    return true;
  };

  // Handle delete cancel
  const fn_ModDelCancel = () => {
    setActiveDiv('list');
    return true;
  };

  // Highlight selected row
  const fn_HighlightSelectedRows = () => {
    // Handled by React state
  };

  // Render the list view
  const renderListView = () => (
    <div>
      {/* Buttons Div */}
      <div id="divButtons" style={{ textAlign: 'center' }}>
        <button
          id="cmdDelete"
          onClick={fnDelete}
          style={{
            backgroundColor: 'Olive',
            color: 'White',
            fontWeight: 'bold',
            width: '120px',
            marginRight: '10px',
            cursor: 'pointer'
          }}
        >
          Delete
        </button>
        &nbsp;&nbsp;
        <button
          id="cmdCount"
          style={{
            backgroundColor: 'Olive',
            color: 'White',
            fontWeight: 'bold',
            width: '120px',
            cursor: 'pointer'
          }}
        >
          Count
        </button>
      </div>

      {/* Title Div */}
      <div id="divTitle" style={{ textAlign: 'center', verticalAlign: 'middle' }}>
        <table style={{ width: '100%' }}>
          <tbody>
            <tr style={{ width: '100%' }}>
              <td style={{ width: '19%' }}></td>
              <td style={{ width: '80%' }}>
                <span style={{
                  textDecoration: 'underline',
                  fontWeight: 'bold',
                  fontSize: 'Large',
                  color: 'Brown'
                }}>
                  Managing Login
                </span>&nbsp;&nbsp;
              </td>
              <td style={{ width: '19%' }}>
                <button
                  id="cmdBack"
                  onClick={() => navigate(-1)}
                  style={{
                    backgroundColor: 'Olive',
                    color: 'White',
                    fontWeight: 'bold',
                    width: '120px',
                    cursor: 'pointer'
                  }}
                >
                  Back
                </button>
                &nbsp;&nbsp;
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* List Div */}
      <div id="divList">
        {/* Filter Div */}
        <div id="divFilter">
          <hr />
          <table style={{ width: '100%' }}>
            <tbody>
              <tr style={{ width: '100%' }}>
                <td style={{ width: '5%' }}></td>
                <td style={{ width: '10%' }}>School</td>
                <td style={{ width: '20%' }}>
                  <select
                    id="cboSchools"
                    value={selectedSchool}
                    onChange={handleSchoolChange}
                    style={{ width: '100%' }}
                  >
                    <option value="">--Select--</option>
                    {schools.map(school => (
                      <option key={school} value={school}>{school}</option>
                    ))}
                  </select>
                </td>
                <td style={{ width: '20%' }}>
                  <input
                    type="radio"
                    id="rdoDtRegn"
                    name="dateType"
                    checked={dateType === 'reg'}
                    onChange={() => handleDateTypeChange('reg')}
                  />
                  Registration Date
                </td>
                <td style={{ width: '35%' }}>
                  <input
                    type="date"
                    id="txtDtRegnFrom"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    style={{ marginRight: '5px' }}
                  />
                  &nbsp;to&nbsp;
                  <input
                    type="date"
                    id="txtDtRegnTo"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                  />
                </td>
                <td rowSpan={3} style={{ width: '10%', verticalAlign: 'middle' }}>
                  <button
                    id="cmdGo"
                    onClick={fnGo}
                    style={{
                      backgroundColor: 'Olive',
                      color: 'White',
                      fontWeight: 'bold',
                      width: '50px',
                      cursor: 'pointer'
                    }}
                  >
                    Go
                  </button>
                  &nbsp;&nbsp;
                </td>
              </tr>
              <tr>
                <td></td>
                <td>Family Name</td>
                <td>
                  <input
                    type="text"
                    id="txtFamily"
                    value={familyName}
                    onChange={(e) => setFamilyName(e.target.value)}
                    style={{ width: '100%' }}
                  />
                </td>
                <td>
                  <input
                    type="radio"
                    id="rdoDtExpiry"
                    name="dateType"
                    checked={dateType === 'expiry'}
                    onChange={() => handleDateTypeChange('expiry')}
                  />
                  Expiry Date
                </td>
                <td>
                  <input
                    type="date"
                    id="txtDtExpiryFrom"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    style={{ marginRight: '5px' }}
                  />
                  &nbsp;to&nbsp;
                  <input
                    type="date"
                    id="txtDtExpiryTo"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                  />
                </td>
              </tr>
              <tr>
                <td></td>
                <td></td>
                <td></td>
                <td>
                  <input
                    type="radio"
                    id="rdoDtLogged"
                    name="dateType"
                    checked={dateType === 'logged'}
                    onChange={() => handleDateTypeChange('logged')}
                  />
                  Logged Date
                </td>
                <td>
                  <input
                    type="date"
                    id="txtDtLoggedFrom"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    style={{ marginRight: '5px' }}
                  />
                  &nbsp;to&nbsp;
                  <input
                    type="date"
                    id="txtDtLoggedTo"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                  />
                </td>
              </tr>
            </tbody>
          </table>
          <hr />
        </div>

        {/* Login List Table */}
        <div
          id="LOGIN_divScrollableTable"
          ref={tableDivRef}
          style={{
            overflow: 'auto',
            border: '1px solid #ccc',
            backgroundColor: 'white'
          }}
        >
          <table
            id="LOGIN_"
            cellPadding="2"
            cellSpacing="0"
            style={{ width: '100%', borderCollapse: 'collapse' }}
          >
            <thead>
              <tr style={{ backgroundColor: '#f0f0f0' }}>
                <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>#</th>
                <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>School Code</th>
                <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Login</th>
                <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>First Name</th>
                <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Last Name</th>
                <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>Registered Date</th>
                <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>Expiry Date</th>
                <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>Logged On</th>
                <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>Category</th>
              </tr>
            </thead>
            <tbody>
              {logins.length > 0 ? (
                logins.map((login, index) => (
                  <tr
                    key={login.Login}
                    onClick={() => handleRowSelect(login)}
                    style={{
                      backgroundColor: selectedLogin?.Login === login.Login ? '#e6f2ff' : index % 2 === 0 ? '#fff' : '#f9f9f9',
                      cursor: 'pointer'
                    }}
                  >
                    <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                      {index + 1}
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>
                      {login.SchoolCode}
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>
                      {login.Login}
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>
                      {login.FName}
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>
                      {login.LName}
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                      {login.RegisteredDate}
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                      {login.ExpiryDate}
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                      {login.LoggedOn}
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                      {login.Category}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={9} style={{ padding: '20px', textAlign: 'center' }}>
                    No logins found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  // Render the delete confirmation view
  const renderDeleteView = () => (
    <div id="divDelete" style={{ textAlign: 'center' }}>
      {/* Dependencies Table */}
      <div
        id="DICDEL_divScrollableTable"
        style={{
          overflow: 'auto',
          border: '1px solid #ccc',
          backgroundColor: 'white',
          marginBottom: '20px',
          maxHeight: '300px'
        }}
      >
        <table
          id="DICDEL_"
          cellPadding="2"
          cellSpacing="0"
          style={{ width: '100%', borderCollapse: 'collapse' }}
        >
          <thead>
            <tr style={{ backgroundColor: 'maroon', color: 'white' }}>
              <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Table Name</th>
              <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Table Description</th>
              <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>Rec Count</th>
              <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Remarks</th>
            </tr>
          </thead>
          <tbody>
            {dependencies.length > 0 ? (
              dependencies.map((dep, index) => (
                <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#fff' : '#f9f9f9' }}>
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>
                    {dep.tableName}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>
                    {dep.tableDesc}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>
                    {dep.recCount}
                  </td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>
                    {dep.remarks}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={4} style={{ padding: '20px', textAlign: 'center' }}>
                  No dependencies found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <br />
      <b><u>Confirm to Delete above Product records permanently from Database</u></b><br />
      Please make sure that the outstanding records have no effect on account of this deletion.<br />
      Do you Really Want to DELETE these Product Records from Database Permanently? <br /><br />

      <button
        id="cmdDeleteOK"
        onClick={fn_DeleteOK}
        style={{
          backgroundColor: 'Red',
          color: 'White',
          fontWeight: 'bold',
          width: '100px',
          marginRight: '20px',
          cursor: 'pointer'
        }}
      >
        OK to Delete
      </button>
      &nbsp;&nbsp;&nbsp;&nbsp;
      <button
        id="cmdDeleteCancel"
        onClick={fn_ModDelCancel}
        style={{
          backgroundColor: 'Green',
          color: 'White',
          fontWeight: 'bold',
          width: '100px',
          cursor: 'pointer'
        }}
      >
        Cancel
      </button>
      <br />
    </div>
  );

  if (loading && activeDiv === 'list' && !schools.length) {
    return <div style={{ textAlign: 'center', padding: '20px' }}>Loading...</div>;
  }

  return (
    <div style={{ margin: '0px' }}>
      {activeDiv === 'list' && renderListView()}
      {activeDiv === 'delete' && renderDeleteView()}

      {/* Hidden fields */}
      <input type="hidden" id="hidAction" value={action} />
      <input type="hidden" id="hidLogin" value={selectedLogin?.Login || ''} />
      <input type="hidden" id="hidFName" value={selectedLogin?.FName || ''} />
      <input type="hidden" id="hidDicFileName" value={dicFileName} />
    </div>
  );
};

export default EL_ManageLogin;